import type { Reducer, Effect } from 'umi'
import { history } from 'umi'
import { updatePassword, getUserInfo, uploadAvatar } from '@/services/system/account'
import { getPermissionList } from '@/services/user'
import { translateDataToTree } from '@/utils/utils'
import type { AccountBriefInfo } from '@/typings/user'

export interface AuthorityType {
  isAdmin: boolean
  isLeads: boolean
  isPm: boolean
  isPartner: boolean
  isLegal: boolean
  isFinance: boolean
  isCFO: boolean
  isMdOrPartner: boolean
}
export interface CurrentUser {
  user: AccountBriefInfo
  authority: AuthorityType
  permissionList: any[]
}

export interface UserModelState {
  currentUser: CurrentUser
}

export interface UserModelType {
  namespace: 'user'
  state: UserModelState
  effects: {
    fetchUpdatePassword: Effect
    fetchUploadAvatar: Effect
    fetchGetUserInfo: Effect
  }
  reducers: {
    saveCurrentUser: Reducer<UserModelState>
    saveMenuList: Reducer<UserModelState>
    removeUserInfo: Reducer<UserModelState>
  }
}

const pageRedirect = (menuList: any[], path: string, code: string = '') => {
  const isExist = menuList.findIndex((li: any) => li.code === code)
  if (isExist >= 0) {
    history.replace({
      pathname: path
    })
  } else {
    history.replace({
      pathname: 'companyInfo/companyDocuments'
    })
  }
}

const UserModel: UserModelType = {
  namespace: 'user',
  state: {
    currentUser: {
      user: {
        log_share_account: []
      },
      permissionList: [],
      authority: {
        isAdmin: false,
        isLeads: false,
        isPm: false,
        isPartner: false,
        isMdOrPartner: false,
        isLegal: false,
        isFinance: false,
        isCFO: false
      }
    }
  },

  effects: {
    // 获取当前用户信息
    *fetchGetUserInfo({ payload }, { call, put }) {
      const res = yield call(getUserInfo)
      // 新的权限列表接口
      const {
        data: { permission }
      } = yield call(getPermissionList)
      yield put({
        type: 'user/saveCurrentUser',
        payload: { ...res.data, permission }
      })
    },

    *fetchUpdatePassword({ payload }, { call, put }) {
      const response = yield call(updatePassword, payload)
      if (response.code === 0) {
        return Promise.resolve()
      }
      return Promise.reject()
    },
    *fetchUploadAvatar({ payload }, { call, put }) {
      const response = yield call(uploadAvatar, payload)
      if (response.code === 0) {
        return Promise.resolve()
      }
      return Promise.reject()
    }
  },

  reducers: {
    removeUserInfo(state) {
      return {
        ...state,
        currentUser: {}
      }
    },
    saveCurrentUser(state, { payload }) {
      const { user, permission = [] } = payload || {}
      const roleList = user.role_code
      let menuList = permission
      // 特殊处理: Yu、franktest账号隐藏掉指定的系统菜单
      if (user && (user.id == 27 || user.id == 68)) {
        const specialCode = [
          '1', // 首页
          '2', // 工作台
          '102', // 我的空间
          '206', // 我的Portfolio
          '215' // 我的PortfolioReview
        ]
        menuList = permission.filter(menu => specialCode.includes(menu.code))
      }
      const userPermissionTree = translateDataToTree(menuList || [], true)
      // 初始页面跳转
      if (window.location.pathname === '/' || window.location.pathname === '/home') {
        if (user && (user.id == 27 || user.id == 68)) {
          pageRedirect(permission, '/workbench/portfolio', '206')
        } else if (roleList.includes('partner')) {
          pageRedirect(permission, '/deal/flows', '303')
        } else if (roleList.includes('leads')) {
          pageRedirect(permission, '/workbench/leads', '201')
        } else if (roleList.includes('pm')) {
          pageRedirect(permission, '/workbench/log', '203')
        } else if (roleList.includes('ex_pm')) {
          pageRedirect(permission, '/workbench/portfolio', '206')
        } else {
          pageRedirect(permission, 'companyInfo/companyDocuments', '408')
        }
      }
      return {
        ...state,
        currentUser: {
          ...payload,
          user,
          userPermissionTree,
          permissionList: permission,
          authority: {
            isAdmin: roleList.includes('administrator'),
            isLeads: roleList.includes('leads'),
            isPm: roleList.includes('pm'),
            isPartner: roleList.includes('partner') || roleList.includes('non_sp_partner'),
            isMdOrPartner: roleList.includes('partner') || roleList.includes('md_sponsor'),
            isLegal: roleList.includes('legal'),
            isFinance: roleList.includes('finance'),
            isCFO: roleList.includes('cfo')
          }
        }
      }
    }
  }
}

export default UserModel
